"use client"

import { format } from "date-fns"
import { CalendarIcon, X } from "lucide-react"
import { FieldPath, FieldValues, Control, useWatch } from "react-hook-form"
import { cn, localToUTCIgnoreTimezone } from "@/shared/lib/utils"
import { Button } from "../ui/button"
import { Calendar } from "../ui/calendar"
import {
    FormControl,
    FormField,
} from "../ui/form"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup } from "../ui/select"
import { useState, useEffect } from "react"
import FormTextInputWrapper, { InputGroupProps } from "./form-input-wrapper"
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'
import { useIsIOS } from "@/shared/hooks/use-is-ios"

/** Format Date to YYYY-MM-DD for native date input value */
function toNativeDateValue(date: Date): string {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
}

/** Parse YYYY-MM-DD string to local Date (avoids timezone shift) */
function fromNativeDateValue(value: string): Date {
    const [y, m, d] = value.split("-").map(Number)
    return new Date(y, m - 1, d)
}

/**
 * Props for the FormDateInput component
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 */
type DateInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
    /** The field name in the form (must be a valid path in TFieldValues) */
    name: TName,
    /** Placeholder text to display when no date is selected */
    placeholder?: string,
    /** Props to pass to the InputGroup wrapper component (excluding icon props) */
    inputGroupProps?: Omit<InputGroupProps, "leftIcon" | "rightIcon">,
    /** React Hook Form control object */
    control: Control<TFieldValues>,
    /** Additional CSS classes for styling */
    className?: string,
    /** Enable year/month dropdowns for easier date selection (useful for date of birth) */
    enableYearMonthDropdowns?: boolean,
    /** Start year for year dropdown (default: 100 years ago) */
    fromYear?: number,
    /** End year for year dropdown (default: current year) */
    toYear?: number,
    /** Show a clear (X) control when a date is selected */
    clearable?: boolean,
}

/**
 * A form date input component that integrates with React Hook Form
 * 
 * This component renders a date picker with a popover calendar interface.
 * It uses date-fns for date formatting and handles UTC timezone conversion.
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 * 
 * @param props - Component props
 * @param props.name - The field name in the form
 * @param props.placeholder - Placeholder text when no date is selected
 * @param props.inputGroupProps - Props for the InputGroup wrapper
 * @param props.control - React Hook Form control object
 * @param props.className - Additional CSS classes
 * 
 * @example
 * ```tsx
 * // In your form component
 * const form = useForm<MyFormType>()
 * 
 * <FormDateInput
 *   name="birthDate"
 *   control={form.control}
 *   placeholder="Select your birth date"
 *   inputGroupProps={{
 *     label: "Birth Date",
 *   }}
 * />
 * ```
 * 
 * @returns JSX.Element - The rendered date input component
 */
function FormDateInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ name, placeholder, inputGroupProps, control, className, enableYearMonthDropdowns = false, fromYear, toYear, clearable = false }: DateInputProps<TFieldValues, TName>) {

    const isIOS = useIsIOS()

    // Check read-only context
    const { isReadOnly } = useReadOnly()
    
    // State to control the popover open/close
    const [isOpen, setIsOpen] = useState(false)
    // State to track the calendar's displayed month (for navigation)
    const [calendarMonth, setCalendarMonth] = useState<Date | undefined>(undefined)
    
    // Watch the field value at component level to use in hooks
    const fieldValue = useWatch({ control, name })
    
    // Get current year and month from field value for display (using local timezone)
    const currentYear = fieldValue ? new Date(fieldValue).getFullYear() : null
    const currentMonth = fieldValue ? new Date(fieldValue).getMonth() + 1 : null
    
    // Initialize calendar month when popover opens
    useEffect(() => {
        if (isOpen && enableYearMonthDropdowns) {
            if (fieldValue) {
                // Use the date directly for calendar display
                const localDate = new Date(fieldValue)
                setCalendarMonth(localDate)
            } else if (currentYear && currentMonth) {
                // Create local date from year/month for calendar display
                setCalendarMonth(new Date(currentYear, currentMonth - 1, 1))
            } else {
                // Only set default if calendarMonth is not already set
                setCalendarMonth((prev) => prev || new Date())
            }
        }
    }, [isOpen, enableYearMonthDropdowns, fieldValue, currentYear, currentMonth])
    
    // Update calendar month when year/month dropdowns change
    useEffect(() => {
        if (isOpen && enableYearMonthDropdowns && currentYear && currentMonth) {
            // Create local date from year/month for calendar display
            const newMonth = new Date(currentYear, currentMonth - 1, 1)
            // Only update if it's different to avoid unnecessary re-renders
            setCalendarMonth((prev) => {
                if (!prev || prev.getTime() !== newMonth.getTime()) {
                    return newMonth
                }
                return prev
            })
        }
    }, [currentYear, currentMonth, isOpen, enableYearMonthDropdowns])
    
    return (
        <FormField
            name={name}
            control={control}
            render={({ field, fieldState }) => {
                // Check if there's a validation error
                const isError = fieldState.error
                const showEmptyHighlight = inputGroupProps?.highlightWhenEmpty && !field.value
                const emptyHighlightClass = showEmptyHighlight ? "!border-[var(--color-pizza-600)] focus-visible:!ring-[var(--color-pizza-600)]" : ""
                const emptyHighlightStyle = showEmptyHighlight ? { backgroundColor: "var(--color-pizza-600)", opacity: 0.3, color: "#000000" } as React.CSSProperties : undefined
                
                // Get current year and month from field value for display in UI (using local timezone)
                const displayYear = field.value ? new Date(field.value).getFullYear() : null
                const displayMonth = field.value ? new Date(field.value).getMonth() + 1 : null
                
                const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
                    e.preventDefault()
                    e.stopPropagation()
                    field.onChange(undefined)
                    setIsOpen(false)
                }

                return (
                    <FormTextInputWrapper inputGroupProps={inputGroupProps}>
                        {isIOS ? (
                            /* Native date input - reliable on iOS Safari (avoids Radix Popover/Select touch issues) */
                            <div className="relative flex w-full items-center gap-1">
                                <input
                                    type="date"
                                    ref={field.ref}
                                    disabled={isReadOnly}
                                    value={field.value ? toNativeDateValue(new Date(field.value)) : ""}
                                    onChange={(e) => {
                                        const val = e.target.value
                                        if (val) {
                                            const date = fromNativeDateValue(val)
                                            field.onChange(
                                                enableYearMonthDropdowns
                                                    ? date
                                                    : localToUTCIgnoreTimezone(date)
                                            )
                                        } else {
                                            field.onChange(undefined)
                                        }
                                    }}
                                    className={cn(
                                        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                                        {
                                            "text-muted-foreground": !field.value,
                                            "border-destructive bg-destructive/10": isError,
                                        },
                                        emptyHighlightClass,
                                        className
                                    )}
                                    style={emptyHighlightStyle}
                                />
                                {clearable && field.value && !isReadOnly && (
                                    <button
                                        type="button"
                                        aria-label="Clear date"
                                        className="absolute right-2 h-5 w-5 rounded-full hover:bg-destructive/10 flex items-center justify-center"
                                        onClick={handleClear}
                                    >
                                        <X className="h-3 w-3 text-muted-foreground" />
                                    </button>
                                )}
                            </div>
                        ) : (
                        <Popover open={isOpen && !isReadOnly} onOpenChange={(open) => !isReadOnly && setIsOpen(open)}>
                            <PopoverTrigger asChild>
                                <FormControl>
                                    {/* Button that triggers the date picker */}
                                    <Button
                                        ref={field.ref}
                                        variant={"outline"}
                                        disabled={isReadOnly}
                                        showLoader={false}
                                        className={cn(
                                            "pl-3 text-left w-full font-normal border-border text-foreground hover:bg-transparent focus-visible:ring-0 data-[state=open]:border-primary focus-visible:ring-offset-0 focus-visible:border-primary",
                                            {
                                                "text-muted-foreground": !field.value,
                                                "bg-destructive/10 hover:bg-destructive/10 focus:border-destructive data-[state=open]:border-destructive": isError,
                                            },
                                            emptyHighlightClass,
                                            className,
                                        )}
                                        style={emptyHighlightStyle}
                                    >
                                        {/* Display selected date or placeholder */}
                                        {field.value ? (
                                            format(field.value, "PPP")
                                        ) : (
                                            <span>{placeholder || "Pick a date"}</span>
                                        )}
                                        <div className="ml-auto flex items-center gap-2">
                                            {clearable && field.value && !isReadOnly && (
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Clear date"
                                                    className="h-4 w-4 rounded-full hover:bg-destructive/10 flex items-center justify-center cursor-pointer"
                                                    onClick={handleClear}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            handleClear(e)
                                                        }
                                                    }}
                                                >
                                                    <X className="h-3 w-3 text-muted-foreground" />
                                                </div>
                                            )}
                                            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                    </Button>
                                </FormControl>
                            </PopoverTrigger>
                            {/* Calendar popover content */}
                            <PopoverContent className="w-auto p-0 z-[200]" align="start">
                                {enableYearMonthDropdowns ? (
                                    <div className="p-3">
                                        {/* Year and Month Selectors */}
                                        <div className="flex gap-2 mb-4">
                                            <div className="flex-1">
                                                <label className="text-xs text-muted-foreground mb-1 block">Year</label>
                                                <Select
                                                    value={displayYear?.toString() || ""}
                                                    onValueChange={(value) => {
                                                        const year = parseInt(value)
                                                        const month = displayMonth || 1
                                                        const day = field.value ? new Date(field.value).getDate() : 1
                                                        // Create date using local timezone to avoid timezone issues
                                                        const newDate = new Date(year, month - 1, day)
                                                        field.onChange(newDate)
                                                    }}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="Select year" />
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-[200px]" style={{ zIndex: 300 }}>
                                                        <SelectGroup>
                                                            {Array.from({ length: (toYear || new Date().getFullYear()) - (fromYear || new Date().getFullYear() - 100) + 1 }, (_, i) => {
                                                                const year = (fromYear || new Date().getFullYear() - 100) + i
                                                                return (
                                                                    <SelectItem key={year} value={year.toString()}>
                                                                        {year}
                                                                    </SelectItem>
                                                                )
                                                            }).reverse()}
                                                        </SelectGroup>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="flex-1">
                                                <label className="text-xs text-muted-foreground mb-1 block">Month</label>
                                                <Select
                                                    value={displayMonth?.toString() || ""}
                                                    onValueChange={(value) => {
                                                        const month = parseInt(value)
                                                        const year = displayYear || new Date().getFullYear()
                                                        const day = field.value ? new Date(field.value).getDate() : 1
                                                        // Create date using local timezone to avoid timezone issues
                                                        const newDate = new Date(year, month - 1, day)
                                                        field.onChange(newDate)
                                                    }}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="Select month" />
                                                    </SelectTrigger>
                                                    <SelectContent style={{ zIndex: 300 }}>
                                                        <SelectGroup>
                                                            {[
                                                                "January", "February", "March", "April", "May", "June",
                                                                "July", "August", "September", "October", "November", "December"
                                                            ].map((month, index) => (
                                                                <SelectItem key={index + 1} value={(index + 1).toString()}>
                                                                    {month}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectGroup>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        {/* Calendar for day selection */}
                                        <Calendar
                                            mode="single"
                                            selected={field.value}
                                            onSelect={(value) => {
                                                if (value) {
                                                    // Calendar returns local date - use it directly as a local date
                                                    // This ensures the clicked day matches what the user sees
                                                    // Create a new Date object using local timezone to avoid timezone shifting
                                                    const year = value.getFullYear()
                                                    const month = value.getMonth()
                                                    const day = value.getDate()
                                                    const newDate = new Date(year, month, day)
                                                    field.onChange(newDate)
                                                    setIsOpen(false)
                                                }
                                            }}
                                            month={calendarMonth}
                                            onMonthChange={setCalendarMonth}
                                            defaultMonth={field.value ? new Date(field.value) : new Date()}
                                            initialFocus
                                        />
                                        {clearable && field.value && (
                                            <div className="mt-2 border-t pt-2">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-full"
                                                    onClick={handleClear}
                                                >
                                                    Clear date
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <Calendar
                                            mode="single"
                                            selected={field.value}
                                            onSelect={(value) => {
                                                // Convert local time to UTC and update field value
                                                field.onChange(localToUTCIgnoreTimezone(value))
                                                // Close the popover after selection
                                                setIsOpen(false)
                                            }}
                                            initialFocus
                                        />
                                        {clearable && field.value && (
                                            <div className="border-t p-2">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-full"
                                                    onClick={handleClear}
                                                >
                                                    Clear date
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </PopoverContent>
                        </Popover>
                        )}
                    </FormTextInputWrapper>
                )
            }}
        />
    )
}

export default FormDateInput